import React from "react";
import { Text, Platform, StyleProp, TextStyle } from "react-native";

interface CrawlableLinkProps {
  /** Internal path, e.g. "/about" or "/opportunity/abc123". */
  to: string;
  /** SPA navigation to run instead of a full page load. */
  onPress: () => void;
  style?: StyleProp<TextStyle>;
  numberOfLines?: number;
  children: React.ReactNode;
}

/**
 * A link that is a real <a href> in the rendered DOM on web, while still
 * navigating through React Navigation.
 *
 * Why it matters: react-native-web renders TouchableOpacity/Text as <div>/<span>
 * unless an href is supplied, so the site's nav and card links were invisible to
 * crawlers — the rendered homepage had zero anchors and detail pages were
 * dead ends. Passing href makes react-native-web emit an anchor; preventDefault
 * on press keeps the SPA behaviour (and leaves cmd/ctrl-click, middle-click, and
 * "open in new tab" working natively, since those never fire onPress).
 */
const CrawlableLink: React.FC<CrawlableLinkProps> = ({
  to,
  onPress,
  style,
  numberOfLines,
  children,
}) => {
  const webProps =
    Platform.OS === "web"
      ? ({ href: to, accessibilityRole: "link" } as any)
      : {};

  return (
    <Text
      {...webProps}
      numberOfLines={numberOfLines}
      onPress={(e: any) => {
        if (
          Platform.OS === "web" &&
          e &&
          typeof e.preventDefault === "function"
        ) {
          e.preventDefault();
        }
        onPress();
      }}
      style={style}
    >
      {children}
    </Text>
  );
};

export default CrawlableLink;
